import { ReadonlyVec2, ReadonlyVec3 } from 'gl-matrix';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import {
    buildAxisAlignedDestGrid,
    buildRotationAroundPivot,
    clampTransformToContent,
    StructureTransformAction,
    TransformedStructure,
} from './transformUtils';

/**
 * Maximum allowed rotation angle in radians (~45 degrees).
 * Large rotations dramatically change the structure's
 * relationship to anatomy and are rarely clinically intended.
 */
const MAX_ROTATION_RAD = Math.PI / 4;

/** Minimum angle change to trigger a grid update (radians). */
const ANGLE_THRESHOLD = 0.001;

/**
 * Action that handles interactive rotation of a structure's SDF
 * texture around the viewing plane's normal, pivoting on the
 * structure's geometric centre.
 *
 * Rotation input uses angular displacement around the structure
 * centre: the user grabs the circle outline of the gizmo and
 * drags along the arc. The rotation stays within the bounds the image
 * sets the structure ({@link clampTransformToContent}).
 *
 * During drag only the grid transform is updated (the grid is
 * rotated in world space while pixel data stays unchanged).
 * On release the pixels are resampled into a new grid aligned
 * with the image grid's orientation and snapped to its voxel
 * lattice.
 */
export class StructureRotateAction extends StructureTransformAction {
    private readonly _roi: RegionOfInterest;

    private _startView?: PlaneView;
    private _centreCanvas?: ReadonlyVec2;
    private _structureCentre?: ReadonlyVec3;
    private _rotationAxis?: ReadonlyVec3;
    private _startAngle?: number;
    private _lastAppliedAngle = 0;

    constructor(roi: RegionOfInterest) {
        super();
        this._roi = roi;
    }

    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const structure = TransformedStructure.of(this._roi);
        if (!structure) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        this._startView = view;
        this._structureCentre = structure.grid.center;
        this._rotationAxis = view.viewDirection;

        this._centreCanvas = view.canvasCoordsFromPatientCoords(
            this._structureCentre
        );

        const canvasCoords = view.clientToCanvas(coordinates);
        this._startAngle = Math.atan2(
            canvasCoords[1] - this._centreCanvas[1],
            canvasCoords[0] - this._centreCanvas[0]
        );

        this.structures = [structure];
        this._lastAppliedAngle = 0;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        const [structure] = this.structures;
        if (
            !this._startView ||
            !this._centreCanvas ||
            !this._structureCentre ||
            this._startAngle === undefined ||
            !this._rotationAxis ||
            !structure
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const canvasCoords = this._startView.clientToCanvas(coordinates);
        const currentAngle = Math.atan2(
            canvasCoords[1] - this._centreCanvas[1],
            canvasCoords[0] - this._centreCanvas[0]
        );

        let deltaAngle = currentAngle - this._startAngle;

        // Normalise to [-PI, PI] to handle wrap-around
        if (deltaAngle > Math.PI) {
            deltaAngle -= 2 * Math.PI;
        } else if (deltaAngle < -Math.PI) {
            deltaAngle += 2 * Math.PI;
        }

        // Clamp to maximum rotation
        deltaAngle = Math.max(
            -MAX_ROTATION_RAD,
            Math.min(MAX_ROTATION_RAD, deltaAngle)
        );

        // Canvas Y is inverted relative to the mathematical
        // convention (Y points down), so negate the angle to make
        // the visual rotation match the drag direction.
        const angle = -deltaAngle;

        if (Math.abs(angle - this._lastAppliedAngle) < ANGLE_THRESHOLD) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true
            );
        }

        const pivot = this._structureCentre;
        const axis = this._rotationAxis;
        const rotationBy = (a: number) =>
            buildRotationAroundPivot(pivot, axis, a);
        const allowedAngle = clampTransformToContent(
            this._lastAppliedAngle,
            angle,
            rotationBy,
            structure.start('rotate')
        );

        // Build forward rotation and apply to the original grid
        const rotMat = rotationBy(allowedAngle);
        const rotatedGrid = structure.grid.transform(rotMat);
        structure.texture.resetGrid(rotatedGrid);
        this._lastAppliedAngle = allowedAngle;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2
    ): EventHandlerResult {
        const pivot = this._structureCentre;
        const axis = this._rotationAxis;
        if (pivot && axis) {
            const angle = this._lastAppliedAngle;
            this.commitStructures(({ grid, texture }) => ({
                srcGrid: grid,
                dstGrid: buildAxisAlignedDestGrid(
                    grid,
                    buildRotationAroundPivot(pivot, axis, angle),
                    texture.grid
                ),
                // The inverse rotation, for the resampler (dst → src)
                patientSrcFromDst: buildRotationAroundPivot(
                    pivot,
                    axis,
                    -angle
                ),
            }));
        }

        this._startView = undefined;
        this._centreCanvas = undefined;
        this._structureCentre = undefined;
        this._rotationAxis = undefined;
        this._startAngle = undefined;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }
}
